import React, { useState, useEffect, useRef } from "react";

import Header from "../Components/Header/Header";
import Footer from "../Components/Footer/Footer";
import { motion, AnimatePresence } from "framer-motion";
import { 

  Check, 
  Loader2, 
  Heart, 
 
  ChevronDown, 

  Sparkles,
 
  MessageSquare,
 
  Send,
  Phone,
  Mail,

  AlertCircle
} from "lucide-react";

// Replace this with your Google App Script URL for saving Congregation Preaching registrations
const GOOGLE_SHEET_WEB_APP_URL = "https://script.google.com/macros/s/AKfycbzdbRx7coiU-99baIsVGkaybHIL50S8gshi7Ya_XLsp9PXHYaT8v7xcWSna2t2H2Eqv/exec";

const CONGREGATION_CONNECT_OPTIONS = [
  { id: "temple_programs", label: "Temple Programs", description: "Attend major festivals and weekly events at the temple" },
  { id: "sunday_feast", label: "Sunday Love Feast", description: "Feast on delicious prasadam and enjoy kirtan, lectures every Sunday" },
  { id: "saturday_satsang", label: "Saturday Satsang", description: "Weekly association and chanting programs on Saturdays" },
  { id: "bhakti_vriksha", label: "Bhakti Vriksha", description: "Small weekly neighborhood spiritual gatherings for study and chanting" },
  { id: "mantra_chanting", label: "Hare Krishna Mahāmantra Chanting", description: "Learn Japa chanting and receive beads/mantra sheets" },
  { id: "life_membership", label: "Life Membership", description: "Become an official patron member of ISKCON worldwide" },
  { id: "spiritual_yatras", label: "Yearly Spiritual Yatras", description: "Group pilgrimages to holy places like Vrindavan, Mayapur, Jagannath Puri" },
  { id: "nitya_seva", label: "Nitya Seva", description: "Contribute regularly for deity worship and daily temple maintenance" },
  { id: "g4u", label: "G4U (Gita for You)", description: "Structured multi-level systematic course on Bhagavad Gita" },
  { id: "online_programs", label: "Online Programs", description: "Interactive lectures and study sessions from the comfort of your home" },
  { id: "children_youth_programs", label: "Children/Youth Programs", description: "Value education, personality development, and spiritual camps for young minds" },
  { id: "gita_sb_learning", label: "Bhagavad Gita / Srimad Bhagavatam Learning", description: "Deep-dive scriptural study courses with certifications" },
  { id: "temple_festivals", label: "Temple Festivals", description: "Volunteer or participate in mega festivals like Janmashtami, Ratha Yatra" },
];

const RATHA_YATRA_KNOW_OPTIONS = [
  "Social Media (Facebook, Instagram, YouTube)",
  "Friends & Family members",
  "Newspaper, Banners & Posters in city",
  "Stall / Festival Visit directly",
  "Other"
];

const CongregationPreaching = () => {
  const [activeSection, setActiveSection] = useState("personal-info");
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Same as Mobile toggle
  const [sameAsMobile, setSameAsMobile] = useState(false);

  // References for scroll to error
  const personalInfoRef = useRef(null);
  const connectRef = useRef(null);
  const spiritualRef = useRef(null);
  const commRef = useRef(null);
  const remarksRef = useRef(null);

  // Form State
  const [form, setForm] = useState({
    // 1. Personal Information
    fullName: "",
    age: "",
    gender: "",
    dob: "",
    mobileNumber: "",
    whatsAppNumber: "",
    emailId: "",
    occupation: "",
    address: "",
    preferredLanguage: "",
    preferredLanguageOther: "",

    // 2. Connect preferences (Array of labels/IDs)
    connectPreferences: [],

    // 3. Spiritual Background
    visitedIskcon: "", 
    knowAboutRathaYatra: "",
    interestedInGita: "",
    homeVisitInvitation: "",
    distributePrasadam: "",
    organizeKirtan: "",
    weeklyGitaSessions: "",
    celebrateJanmashtami: "",

    // 4. Communication Preference
    communicationPreference: "",

    // 5. Remarks
    remarks: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update WhatsApp if Same As Mobile is toggled
  useEffect(() => {
    if (sameAsMobile) {
      setForm(prev => ({ ...prev, whatsAppNumber: prev.mobileNumber }));
      setErrors(prev => ({ ...prev, whatsAppNumber: "" }));
    }
  }, [sameAsMobile, form.mobileNumber]);

  // Handle intersection observer to highlight active section on desktop side navigation
  useEffect(() => {
    const sectionIds = [
      "personal-info",
      "connect-preferences",
      "spiritual-background",
      "communication-preferences",
      "remarks"
    ];

    const observerOptions = {
      root: null,
      rootMargin: "-10% 0px -70% 0px",
      threshold: 0
    };

    const handleIntersect = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    sectionIds.forEach(id => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => {
      sectionIds.forEach(id => {
        const element = document.getElementById(id);
        if (element) observer.unobserve(element);
      });
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Mobile/WhatsApp length restriction (10 digits)
    if ((name === "mobileNumber" || name === "whatsAppNumber") && !/^\d*$/.test(value)) return;
    if ((name === "mobileNumber" || name === "whatsAppNumber") && value.length > 10) return;
    if (name === "age" && !/^\d*$/.test(value)) return;

    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleGenderSelect = (gender) => {
    setForm(prev => ({ ...prev, gender }));
    setErrors(prev => ({ ...prev, gender: "" }));
  };

  const handleLanguageSelect = (e) => {
    const lang = e.target.value;
    setForm(prev => ({ 
      ...prev, 
      preferredLanguage: lang, 
      preferredLanguageOther: lang === "Other" ? prev.preferredLanguageOther : "" 
    }));
    setErrors(prev => ({ ...prev, preferredLanguage: "", preferredLanguageOther: "" }));
  };

  const handleConnectToggle = (id) => {
    setForm(prev => {
      const isSelected = prev.connectPreferences.includes(id);
      const updated = isSelected 
        ? prev.connectPreferences.filter(item => item !== id)
        : [...prev.connectPreferences, id];
      return { ...prev, connectPreferences: updated };
    });
    setErrors(prev => ({ ...prev, connectPreferences: "" }));
  };

  const handleSpiritualQuestionSelect = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleCommPreferenceSelect = (pref) => {
    setForm(prev => ({ ...prev, communicationPreference: pref }));
    setErrors(prev => ({ ...prev, communicationPreference: "" }));
  };

  const handleSameAsMobileToggle = () => {
    setSameAsMobile(!sameAsMobile);
  };

  // Smooth scroll helper
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // 1. Personal Information validation
    if (!form.fullName.trim()) newErrors.fullName = "Full Name is required.";
    if (!form.age.trim()) newErrors.age = "Age is required.";
    if (!form.gender) newErrors.gender = "Gender is required.";
    
    if (!form.mobileNumber) newErrors.mobileNumber = "Mobile Number is required.";
    else if (form.mobileNumber.length !== 10) newErrors.mobileNumber = "Enter a valid 10-digit mobile number.";

    if (!form.whatsAppNumber) newErrors.whatsAppNumber = "WhatsApp Number is required.";
    else if (form.whatsAppNumber.length !== 10) newErrors.whatsAppNumber = "Enter a valid 10-digit WhatsApp number.";

    if (!form.emailId.trim()) newErrors.emailId = "Email ID is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailId)) newErrors.emailId = "Enter a valid email address.";

    if (!form.occupation.trim()) newErrors.occupation = "Occupation is required.";
    if (!form.address.trim()) newErrors.address = "Address is required.";
    if (!form.preferredLanguage) newErrors.preferredLanguage = "Preferred Language is required.";
    if (form.preferredLanguage === "Other" && !form.preferredLanguageOther.trim()) {
      newErrors.preferredLanguageOther = "Please specify your preferred language.";
    }

    // 2. Connect Preferences
    if (form.connectPreferences.length === 0) {
      newErrors.connectPreferences = "Please select at least one way to connect with ISKCON.";
    }

    // 3. Spiritual Background
    if (!form.visitedIskcon) newErrors.visitedIskcon = "Please select an answer.";
    if (!form.knowAboutRathaYatra) newErrors.knowAboutRathaYatra = "Please select how you knew about Ratha Yatra.";
    if (!form.interestedInGita) newErrors.interestedInGita = "Please select an answer.";
    if (!form.homeVisitInvitation) newErrors.homeVisitInvitation = "Please select an answer.";
    if (!form.distributePrasadam) newErrors.distributePrasadam = "Please select an answer.";
    if (!form.organizeKirtan) newErrors.organizeKirtan = "Please select an answer.";
    if (!form.weeklyGitaSessions) newErrors.weeklyGitaSessions = "Please select an answer.";
    if (!form.celebrateJanmashtami) newErrors.celebrateJanmashtami = "Please select an answer.";

    // 4. Communication Preference
    if (!form.communicationPreference) {
      newErrors.communicationPreference = "Preferred contact method is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Find the first error block and scroll to it
      const errorKeys = Object.keys(errors);
      let targetRef = null;

      // Map error fields to section elements for smooth scrolling
      if (
        errors.fullName || errors.age || errors.gender || errors.mobileNumber || 
        errors.whatsAppNumber || errors.emailId || errors.occupation || 
        errors.address || errors.preferredLanguage || errors.preferredLanguageOther
      ) {
        targetRef = personalInfoRef;
      } else if (errors.connectPreferences) {
        targetRef = connectRef;
      } else if (
        errors.visitedIskcon || errors.knowAboutRathaYatra || errors.interestedInGita || 
        errors.homeVisitInvitation || errors.distributePrasadam || errors.organizeKirtan || 
        errors.weeklyGitaSessions || errors.celebrateJanmashtami
      ) {
        targetRef = spiritualRef;
      } else if (errors.communicationPreference) {
        targetRef = commRef;
      }

      if (targetRef && targetRef.current) {
        targetRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        // Fallback: Scroll to top of error
        const firstErrorElement = document.querySelector(".text-red");
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    setLoading(true);

    // Build human-readable selected connect preferences string
    const selectedConnectLabels = form.connectPreferences
      .map(id => CONGREGATION_CONNECT_OPTIONS.find(opt => opt.id === id)?.label || id)
      .join(", ");

    const payload = {
      // 1. Personal
      fullName: form.fullName,
      age: form.age,
      gender: form.gender,
      dob: form.dob || "N/A",
      mobileNumber: form.mobileNumber,
      whatsAppNumber: form.whatsAppNumber,
      emailId: form.emailId,
      occupation: form.occupation,
      address: form.address,
      preferredLanguage: form.preferredLanguage === "Other" ? form.preferredLanguageOther : form.preferredLanguage,

      // 2. Connect preferences
      connectPreferences: selectedConnectLabels,

      // 3. Spiritual Background
      visitedIskcon: form.visitedIskcon,
      knowAboutRathaYatra: form.knowAboutRathaYatra,
      interestedInGita: form.interestedInGita,
      homeVisitInvitation: form.homeVisitInvitation,
      distributePrasadam: form.distributePrasadam,
      organizeKirtan: form.organizeKirtan,
      weeklyGitaSessions: form.weeklyGitaSessions,
      celebrateJanmashtami: form.celebrateJanmashtami,

      // 4. Comm Preference
      communicationPreference: form.communicationPreference,

      // 5. Remarks
      remarks: form.remarks || "N/A",



      registrationDate: new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
    };

    try {
      if (GOOGLE_SHEET_WEB_APP_URL) {
        const formBody = new URLSearchParams(payload).toString();
        await fetch(GOOGLE_SHEET_WEB_APP_URL, {
          method: "POST",
          mode: "no-cors",
          body: formBody,
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
      } else {
        // Simulate loading response time for premium feeling
        await new Promise(resolve => setTimeout(resolve, 1500));
        console.log("Mock submission success:", payload);
      }

      setShowSuccessModal(true);
    } catch (err) {
      console.error(err);
      alert("Something went wrong while submitting. Please check your network and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    setForm({
      fullName: "",
      age: "",
      gender: "",
      dob: "",
      mobileNumber: "",
      whatsAppNumber: "",
      emailId: "",
      occupation: "",
      address: "",
      preferredLanguage: "",
      preferredLanguageOther: "",
      connectPreferences: [],
      visitedIskcon: "",
      knowAboutRathaYatra: "",
      interestedInGita: "",
      homeVisitInvitation: "",
      distributePrasadam: "",
      organizeKirtan: "",
      weeklyGitaSessions: "",
      celebrateJanmashtami: "",
      communicationPreference: "",
      remarks: "",
    });
    setSameAsMobile(false);
    setErrors({});
    setActiveSection("personal-info");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Header />
      <div className="bg-[#FAF7F0] min-h-screen flex flex-col font-open-sans relative overflow-hidden">
        
        {/* Floating Decorative Elements */}
        {/* Rotating Traditional Mandala */}
        <div className="absolute top-5 right-[-100px] opacity-[0.02] select-none pointer-events-none w-[400px] h-[400px] animate-[spin_120s_linear_infinite] hidden lg:block">
          <svg viewBox="0 0 100 100" className="w-full h-full text-orange fill-current">
            <circle cx="50" cy="50" r="45" stroke="currentColor" strokeWidth="1" fill="none" />
            <circle cx="50" cy="50" r="5" />
            {[...Array(16)].map((_, i) => (
              <path
                key={i}
                d="M50 15 L52 35 L48 35 Z"
                transform={`rotate(${i * 22.5} 50 50)`}
              />
            ))}
          </svg>
        </div>

        {/* Floating Lotus Left */}
        <div className="absolute bottom-20 left-[-80px] opacity-[0.02] select-none pointer-events-none w-[300px] h-[300px] hidden lg:block">
          <svg viewBox="0 0 100 100" className="w-full h-full text-orange fill-current animate-pulse">
            <path d="M50 10 C55 30 75 35 75 50 C75 65 55 70 50 90 C45 70 25 65 25 50 C25 35 45 30 50 10 Z" />
            <path d="M50 25 C53 40 68 43 68 50 C68 57 53 60 50 75 C47 60 32 57 32 50 C32 43 47 40 50 25 Z" opacity="0.6" />
          </svg>
        </div>

        {/* Hero Section (Compact spacing, larger typography) */}
        <div className="relative overflow-hidden bg-gradient-to-br from-darkred via-[#7D1215] to-[#B01F24] text-white py-12 md:py-16 px-4 sm:px-6 lg:px-8 text-center shadow-2xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.08)_0%,transparent_65%)] pointer-events-none" />
          
          <div className="max-w-5xl mx-auto relative z-10 space-y-3 lg:space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-sm sm:text-base tracking-widest font-semibold uppercase shadow-inner">
              <Sparkles className="w-4.5 h-4.5 text-orange animate-pulse" />
              RATHA YATRA 2026
            </div>

            <p className="text-lg sm:text-xl font-extrabold text-orange tracking-widest uppercase font-josefin">
              WELCOME TO ISKCON
            </p>

            <p className="font-dm-serif text-3xl sm:text-4xl lg:text-4xl lg:font-bold lg:tracking-tight leading-none text-white drop-shadow-lg">
              Congregation Preaching Registration Form
            </p>

            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-yellow-400 italic">
              Take One Step Towards Krishna
            </p>

            <p className="text-lg sm:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              We are here to help you begin your spiritual journey. Register today and stay connected.
            </p>

            {/* Welcome Information Card (Tighter padding & spacing) */}
            <motion.div 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-4 bg-white/10 backdrop-blur-md border border-white/25 rounded-3xl p-2 lg:p-5 sm:p-6 text-yellow-100 max-w-4xl mx-auto shadow-xl"
            >
              <p className="text-base md:text-xl font-bold leading-relaxed">
                Hare Krishna! Thank you for visiting our Ratha Yatra stall. Please complete this form so we can invite you to devotional programs and stay connected.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Form Container (Reduced padding & gap) */}
        <div className=" mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-grow w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
            
            {/* Desktop Left Column: Sticky Side Navigation / Progress Panel (Tighter spacing) */}
            <div className="hidden lg:block lg:col-span-3 sticky top-28 space-y-4">
              <div className="bg-white rounded-3xl px-4 py-3 shadow-md border border-orange/10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2.5 h-full bg-gradient-to-b from-darkred to-orange" />
                
                <h3 className="font-semibold text-darkred text-xl mb-2 pl-2">Registration Sections</h3>
                
                <nav className="space-y-3.5 pl-2 text-lg">
                  <button
                    onClick={() => scrollToSection("personal-info")}
                    className={`flex items-center gap-3.5 w-full text-left transition-colors ${
                      activeSection === "personal-info" ? "text-orange font-black text-lg" : "text-gray-500 hover:text-darkred"
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center  border ${
                      activeSection === "personal-info" ? "bg-orange text-white border-orange" : "border-gray-300"
                    }`}>1</span>
                    Personal Info
                  </button>

                  <button
                    onClick={() => scrollToSection("connect-preferences")}
                    className={`flex items-center gap-3.5 w-full text-left transition-colors ${
                      activeSection === "connect-preferences" ? "text-orange font-black text-lg" : "text-gray-500 hover:text-darkred"
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm border ${
                      activeSection === "connect-preferences" ? "bg-orange text-white border-orange" : "border-gray-300"
                    }`}>2</span>
                    Connect Preferences
                  </button>

                  <button
                    onClick={() => scrollToSection("spiritual-background")}
                    className={`flex items-center gap-3.5 w-full text-left transition-colors ${
                      activeSection === "spiritual-background" ? "text-orange font-black text-lg" : "text-gray-500 hover:text-darkred"
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm border ${
                      activeSection === "spiritual-background" ? "bg-orange text-white border-orange" : "border-gray-300"
                    }`}>3</span>
                    Spiritual Background
                  </button>

                  <button
                    onClick={() => scrollToSection("communication-preferences")}
                    className={`flex items-center gap-3.5 w-full text-left transition-colors ${
                      activeSection === "communication-preferences" ? "text-orange font-black text-lg" : "text-gray-500 hover:text-darkred"
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm border ${
                      activeSection === "communication-preferences" ? "bg-orange text-white border-orange" : "border-gray-300"
                    }`}>4</span>
                    Communication
                  </button>

                  <button
                    onClick={() => scrollToSection("remarks")}
                    className={`flex items-center gap-3.5 w-full text-left transition-colors ${
                      activeSection === "remarks" ? "text-orange font-black text-lg" : "text-gray-500 hover:text-darkred"
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm border ${
                      activeSection === "remarks" ? "bg-orange text-white border-orange" : "border-gray-300"
                    }`}>5</span>
                    Remarks
                  </button>


                </nav>
              </div>

              {/* Devotional Quote Display Card */}
              <div className="rounded-3xl bg-gradient-to-br from-darkred to-rose-950 text-white p-3 border border-rose-900 shadow-md relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(222,125,13,0.15)_0%,transparent_60%)] pointer-events-none" />
                <div className="relative z-10 text-center space-y-3">
                  <Heart className="w-6 h-6 text-yellow-400 mx-auto fill-yellow-400/20" />
                  <p className="text-lg  italic text-yellow-300">
                    "Hare Krishna Hare Krishna <br /> Krishna Krishna Hare Hare <br /> Hare Rama Hare Rama <br /> Rama Rama Hare Hare"
                  </p>
                  <p className="text-sm text-rose-200">
                    Chant these holy names and transform your life with divine joy.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column / Center Column: Form Inputs */}
            <div className="col-span-1 lg:col-span-9 space-y-6 lg:h-[calc(100vh-160px)] lg:overflow-y-auto no-scrollbar">
              <form onSubmit={handleSubmit} className="space-y-6">

                {/* SECTION 1: Personal Information (Compact padding, larger text) */}
                <div 
                  id="personal-info"
                  ref={personalInfoRef}
                  className="bg-white rounded-3xl shadow-xl shadow-amber-900/5 border border-amber-500/10 p-3 sm:p-2 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red via-orange to-yellow-500" />
                  
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-orange/10 text-orange flex items-center justify-center font-semibold text-lg flex-shrink-0">
                      1
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-black text-darkred mb-0">Personal Information</p>
                      <p className="text-sm text-gray-500">Please provide your details below.</p>
                    </div>
                  </div>

                  <div className=" space-y-1 lg:space-y-3">
                    
                    {/* Full Name */}
                    <div className="relative">
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={form.fullName}
                        onChange={handleInputChange}
                        placeholder="Full Name *"
                        className={`peer w-full bg-[#FAF9F6] text-lg md:text-xl font-normal text-gray-800 border rounded-xl px-4 py-3 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 ${
                          errors.fullName ? "border-red focus:border-red" : "border-gray-200 focus:border-orange"
                        }`}
                      />
                      {errors.fullName && (
                        <p className="text-red text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.fullName}
                        </p>
                      )}
                    </div>

                    {/* Age, Gender & DOB Group */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-1 lg:gap-4">
                      
                      {/* Age */}
                      <div className="relative">
                        <input
                          type="text"
                          id="age"
                          name="age"
                          value={form.age}
                          onChange={handleInputChange}
                          placeholder="Age *"
                          className={`peer w-full bg-[#FAF9F6] text-lg md:text-xl font-normal text-gray-800 border rounded-xl px-4 py-3 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 ${
                            errors.age ? "border-red focus:border-red" : "border-gray-200 focus:border-orange"
                          }`}
                        />
                        {errors.age && (
                          <p className="text-red text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.age}
                          </p>
                        )}
                      </div>

                      {/* Gender (Segmented controls) */}
                      <div className="max-md:mb-3">
                        <div className="flex bg-[#FAF9F6] border border-gray-200 rounded-xl p-1 h-[50px] items-center relative">
                          <button
                            type="button"
                            onClick={() => handleGenderSelect("Male")}
                            className={`flex-1 text-center py-2 text-base md:text-lg  rounded-lg transition-all ${
                              form.gender === "Male" 
                                ? "bg-darkred text-white shadow" 
                                : "text-gray-500 hover:text-darkred"
                            }`}
                          >
                            Male
                          </button>
                          <button
                            type="button"
                            onClick={() => handleGenderSelect("Female")}
                            className={`flex-1 text-center py-2  md:text-lg  rounded-lg transition-all ${
                              form.gender === "Female" 
                                ? "bg-darkred text-white shadow" 
                                : "text-gray-500 hover:text-darkred"
                            }`}
                          >
                            Female
                          </button>
                        </div>
                        {errors.gender && (
                          <p className="text-red text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.gender}
                          </p>
                        )}
                      </div>

                      {/* Date of Birth (Optional) */}
                      <div className="relative">
                        <input
                          type="date"
                          id="dob"
                          name="dob"
                          value={form.dob}
                          onChange={handleInputChange}
                          placeholder="Date of Birth (Optional)"
                          className="peer w-full bg-[#FAF9F6] text-base md:text-lg font-normal text-gray-800 border border-gray-200 rounded-xl px-4 py-2 outline-none transition-all duration-300 focus:border-orange focus:ring-2 focus:ring-orange/20 h-[50px]"
                        />
                      </div>

                    </div>

                    {/* Mobile & WhatsApp Numbers */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 lg:gap-4">
                      
                      {/* Mobile Number */}
                      <div className="relative">
                        <div className="flex items-center">
                          <div className="flex items-center border border-r-0 border-gray-200 bg-gray-50 text-gray-500 rounded-l-xl px-3.5 text-base md:text-lg h-[50px] font-normal">
                            <span>+91</span>
                          </div>
                          <input
                            type="tel"
                            id="mobileNumber"
                            name="mobileNumber"
                            value={form.mobileNumber}
                            onChange={handleInputChange}
                            placeholder="Mobile Number *"
                            className={`peer w-full bg-[#FAF9F6] text-lg md:text-xl font-normal text-gray-800 border rounded-r-xl px-4 py-3 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 h-[50px] ${
                              errors.mobileNumber ? "border-red focus:border-red" : "border-gray-200 focus:border-orange"
                            }`}
                          />
                        </div>
                        {errors.mobileNumber && (
                          <p className="text-red text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.mobileNumber}
                          </p>
                        )}
                      </div>

                      {/* WhatsApp Number */}
                      <div className="relative">
                        <div className="flex items-center">
                          <div className="flex items-center border border-r-0 border-gray-200 bg-gray-50 text-gray-500 rounded-l-xl px-3.5 text-base md:text-lg h-[50px] font-normal">
                            <span>+91</span>
                          </div>
                          <input
                            type="tel"
                            id="whatsAppNumber"
                            name="whatsAppNumber"
                            value={form.whatsAppNumber}
                            onChange={handleInputChange}
                            disabled={sameAsMobile}
                            placeholder="WhatsApp Number *"
                            className={`peer w-full bg-[#FAF9F6] text-lg md:text-xl font-normal text-gray-800 border rounded-r-xl px-4 py-3 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 h-[50px] disabled:opacity-70 ${
                              errors.whatsAppNumber ? "border-red focus:border-red" : "border-gray-200 focus:border-orange"
                            }`}
                          />
                        </div>

                        {/* Same as Mobile Checkbox Toggle */}
                        <div className="mt-1 flex items-center justify-between">
                          <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input 
                              type="checkbox"
                              checked={sameAsMobile}
                              onChange={handleSameAsMobileToggle}
                              className="w-4 h-4 text-orange border-gray-300 rounded focus:ring-orange/30 cursor-pointer"
                            />
                            <span className="text-xs md:text-sm text-gray-500 font-normal">Same as Mobile Number</span>
                          </label>
                        </div>

                        {errors.whatsAppNumber && (
                          <p className="text-red text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.whatsAppNumber}
                          </p>
                        )}
                      </div>

                    </div>

                    {/* Email ID & Occupation */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 lg:gap-4">
                      
                      {/* Email ID */}
                      <div className="relative">
                        <input
                          type="email"
                          id="emailId"
                          name="emailId"
                          value={form.emailId}
                          onChange={handleInputChange}
                          placeholder="Email ID *"
                          className={`peer w-full bg-[#FAF9F6] text-lg md:text-xl font-normal text-gray-800 border rounded-xl px-4 py-3 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 ${
                            errors.emailId ? "border-red focus:border-red" : "border-gray-200 focus:border-orange"
                          }`}
                        />
                        {errors.emailId && (
                          <p className="text-red text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.emailId}
                          </p>
                        )}
                      </div>

                      {/* Occupation */}
                      <div className="relative">
                        <input
                          type="text"
                          id="occupation"
                          name="occupation"
                          value={form.occupation}
                          onChange={handleInputChange}
                          placeholder="Occupation *"
                          className={`peer w-full bg-[#FAF9F6] text-lg md:text-xl font-normal text-gray-800 border rounded-xl px-4 py-3 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 ${
                            errors.occupation ? "border-red focus:border-red" : "border-gray-200 focus:border-orange"
                          }`}
                        />
                        {errors.occupation && (
                          <p className="text-red text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.occupation}
                          </p>
                        )}
                      </div>

                    </div>

                    {/* Preferred Language */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      
                      {/* Preferred Language Select */}
                      <div className="relative max-md:mb-3">
                        <select
                          id="preferredLanguage"
                          name="preferredLanguage"
                          value={form.preferredLanguage}
                          onChange={handleLanguageSelect}
                          className={`peer w-full bg-[#FAF9F6] text-base md:text-lg font-normal text-gray-800 border rounded-xl px-4 py-2 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 appearance-none h-[50px] ${
                            errors.preferredLanguage ? "border-red focus:border-red" : "border-gray-200 focus:border-orange"
                          }`}
                        >
                          <option value="" hidden>Preferred Language *</option>
                          <option value="English">English</option>
                          <option value="Telugu">Telugu</option>
                          <option value="Hindi">Hindi</option>
                          <option value="Other">Other</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-orange">
                          <ChevronDown className="w-5 h-5" />
                        </div>
                        {errors.preferredLanguage && (
                          <p className="text-red text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.preferredLanguage}
                          </p>
                        )}
                      </div>

                      {/* Language Other (Rendered conditionally with Framer Motion) */}
                      <AnimatePresence>
                        {form.preferredLanguage === "Other" && (
                          <motion.div 
                            initial={{ opacity: 0, height: 0, scale: 0.95 }}
                            animate={{ opacity: 1, height: "auto", scale: 1 }}
                            exit={{ opacity: 0, height: 0, scale: 0.95 }}
                            className="relative"
                          >
                            <input
                              type="text"
                              id="preferredLanguageOther"
                              name="preferredLanguageOther"
                              value={form.preferredLanguageOther}
                              onChange={handleInputChange}
                              placeholder="Specify Language *"
                              className={`peer w-full bg-[#FAF9F6] text-lg md:text-xl font-normal text-gray-800 border rounded-xl px-4 py-3 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 h-[50px] ${
                                errors.preferredLanguageOther ? "border-red focus:border-red" : "border-gray-200 focus:border-orange"
                              }`}
                            />
                            {errors.preferredLanguageOther && (
                              <p className="text-red text-sm mt-1 flex items-center gap-1">
                                <AlertCircle className="w-4 h-4" />
                                {errors.preferredLanguageOther}
                              </p>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>

                    </div>

                    {/* Residential Address */}
                    <div className="relative">
                      <textarea
                        id="address"
                        name="address"
                        value={form.address}
                        onChange={handleInputChange}
                        placeholder="Address (Residential) *"
                        rows={3}
                        className={`peer w-full bg-[#FAF9F6] text-lg md:text-xl font-normal text-gray-800 border rounded-xl px-4 py-3 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 resize-none ${
                          errors.address ? "border-red focus:border-red" : "border-gray-200 focus:border-orange"
                        }`}
                      />
                      {errors.address && (
                        <p className="text-red text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.address}
                        </p>
                      )}
                    </div>

                  </div>
                </div>

                {/* SECTION 2: How would you like to connect with ISKCON Vijayawada? (Reduced gaps, larger text) */}
                <div 
                  id="connect-preferences"
                  ref={connectRef}
                  className="bg-white rounded-3xl shadow-xl shadow-amber-900/5 border border-amber-500/10 p-3 sm:p-2 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red via-orange to-yellow-500" />
                  
                  <div className="flex items-center gap-3.5 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-orange/10 text-orange flex items-center justify-center font-semibold text-lg flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h2 className="text-lg md:text-3xl font-black text-darkred">How would you like to connect with ISKCON Vijayawada?</h2>
                      <p className="text-sm text-gray-500">Select all program interest cards that apply to you.</p>
                    </div>
                  </div>

                  {errors.connectPreferences && (
                    <div className="mb-3 bg-red/5 text-red px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 border border-red/10">
                      <AlertCircle className="w-4.5 h-4.5" />
                      {errors.connectPreferences}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-3">
                    {CONGREGATION_CONNECT_OPTIONS.map((option) => {
                      const isSelected = form.connectPreferences.includes(option.id);
                      return (
                        <div
                          key={option.id}
                          onClick={() => handleConnectToggle(option.id)}
                          className={`border rounded-2xl p-3 lg:p-4 cursor-pointer transition-all duration-300 group flex flex-col justify-between h-full relative overflow-hidden shadow-sm hover:shadow-md ${
                            isSelected 
                              ? "border-orange bg-orange/5 ring-1 ring-orange" 
                              : "border-gray-100 hover:border-orange/40 hover:bg-[#FAF9F6]"
                          }`}
                        >
                          <div className="space-y-1.5 relative z-10">
                            <div className="flex items-start justify-between gap-3">
                              <h4 className={`text-lg md:text-xl font-black leading-tight ${isSelected ? "text-darkred" : "text-gray-800"}`}>
                                {option.label}
                              </h4>
                              
                              <div className={`w-6 h-6 rounded-md flex items-center justify-center border flex-shrink-0 transition-colors ${
                                isSelected ? "bg-orange border-orange text-white" : "border-gray-300 group-hover:border-orange"
                              }`}>
                                {isSelected && <Check className="w-4 h-4 stroke-[3px]" />}
                              </div>
                            </div>
                            <p className="text-sm md:text-base text-gray-500 leading-normal">
                              {option.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* SECTION 3: Spiritual Background (Compact layout, larger labels) */}
                <div 
                  id="spiritual-background"
                  ref={spiritualRef}
                  className="bg-white rounded-3xl shadow-xl shadow-amber-900/5 border border-amber-500/10 max-md:p-3 md:p-5 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red via-orange to-yellow-500" />
                  
                  <div className="flex items-center gap-3.5 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-orange/10 text-orange flex items-center justify-center font-semibold text-lg flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h2 className="text-lg lg:text-3xl font-black text-darkred">Spiritual Background</h2>
                      <p className="text-sm text-gray-500">Tell us a bit about your experiences and interests.</p>
                    </div>
                  </div>

                  <div className="space-y-2 lg:space-y-4">

                    {/* Question 1: Have you visited ISKCON before? */}
                    <div className="border-b border-gray-100 pb-3">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 lg:gap-3">
                        <label className="text-lg md:text-xl font-black text-gray-800 md:max-w-xl">
                          Have you visited ISKCON before?
                        </label>
                        <div className="flex bg-[#FAF9F6] border border-gray-200 rounded-xl p-1 w-full md:w-[200px] h-[44px] items-center relative flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("visitedIskcon", "Yes")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.visitedIskcon === "Yes" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("visitedIskcon", "No")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.visitedIskcon === "No" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      {errors.visitedIskcon && (
                        <p className="text-red text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.visitedIskcon}
                        </p>
                      )}
                    </div>

                    {/* Question 2: How did you know about Ratha Yatra? */}
                    <div className="border-b border-gray-100 pb-4">
                      <label className="text-lg md:text-xl font-black text-gray-800 block mb-1 lg:mb-3">
                        How did you know about Ratha Yatra?
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {RATHA_YATRA_KNOW_OPTIONS.map((option) => {
                          const isSelected = form.knowAboutRathaYatra === option;
                          return (
                            <div
                              key={option}
                              onClick={() => handleSpiritualQuestionSelect("knowAboutRathaYatra", option)}
                              className={`border rounded-xl px-1.5 py-2 lg:p-3 cursor-pointer transition-all flex items-center gap-3 text-base md:text-lg font-black leading-snug ${
                                isSelected 
                                  ? "border-orange bg-orange/5 text-darkred ring-1 ring-orange" 
                                  : "border-gray-200 hover:border-orange/50 hover:bg-[#FAF9F6] text-gray-700"
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center border flex-shrink-0 transition-colors ${
                                isSelected ? "border-orange text-orange" : "border-gray-300"
                              }`}>
                                {isSelected && <div className="w-2.5 h-2.5 bg-orange rounded-full" />}
                              </div>
                              {option}
                            </div>
                          );
                        })}
                      </div>
                      {errors.knowAboutRathaYatra && (
                        <p className="text-red text-sm mt-2 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.knowAboutRathaYatra}
                        </p>
                      )}
                    </div>

                    {/* Question 3: Are you interested in learning Bhagavad-gītā? */}
                    <div className="border-b border-gray-100 pb-3">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 lg:gap-3">
                        <label className="text-lg md:text-xl font-black text-gray-800 md:max-w-xl">
                          Are you interested in learning Bhagavad-gītā?
                        </label>
                        <div className="flex bg-[#FAF9F6] border border-gray-200 rounded-xl p-1 w-full md:w-[200px] h-[44px] items-center relative flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("interestedInGita", "Yes")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.interestedInGita === "Yes" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("interestedInGita", "No")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.interestedInGita === "No" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      {errors.interestedInGita && (
                        <p className="text-red text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.interestedInGita}
                        </p>
                      )}
                    </div>

                    {/* Question 4: Would you like a Home Visit / Bhakti Vriksha invitation? */}
                    <div className="border-b border-gray-100 pb-3">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 lg:gap-3">
                        <label className="text-lg md:text-xl font-black text-gray-800 md:max-w-xl">
                          Would you like a Home Visit / Bhakti Vriksha invitation?
                        </label>
                        <div className="flex bg-[#FAF9F6] border border-gray-200 rounded-xl p-1 w-full md:w-[200px] h-[44px] items-center relative flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("homeVisitInvitation", "Yes")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.homeVisitInvitation === "Yes" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("homeVisitInvitation", "No")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.homeVisitInvitation === "No" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      {errors.homeVisitInvitation && (
                        <p className="text-red text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.homeVisitInvitation}
                        </p>
                      )}
                    </div>

                    {/* Question 5: Would you like to distribute Krishna Prasadam (Pure Vegetarian) at your functions or home? */}
                    <div className="border-b border-gray-100 pb-3">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 lg:gap-3">
                        <label className="text-lg md:text-xl font-black text-gray-800 md:max-w-xl">
                          Would you like to distribute Krishna Prasadam (Pure Vegetarian) at your functions or home?
                        </label>
                        <div className="flex bg-[#FAF9F6] border border-gray-200 rounded-xl p-1 w-full md:w-[200px] h-[44px] items-center relative flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("distributePrasadam", "Yes")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.distributePrasadam === "Yes" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("distributePrasadam", "No")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.distributePrasadam === "No" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      {errors.distributePrasadam && (
                        <p className="text-red text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.distributePrasadam}
                        </p>
                      )}
                    </div>

                    {/* Question 6: Would you like to organize a Kirtan program during Karthik/Damodar month in your home or locality? */}
                    <div className="border-b border-gray-100 pb-3">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 lg:gap-3">
                        <label className="text-lg md:text-xl font-black text-gray-800 md:max-w-xl">
                          Would you like to organize a Kirtan program during Karthik/Damodar month in your home or locality?
                        </label>
                        <div className="flex bg-[#FAF9F6] border border-gray-200 rounded-xl p-1 w-full md:w-[200px] h-[44px] items-center relative flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("organizeKirtan", "Yes")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.organizeKirtan === "Yes" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("organizeKirtan", "No")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.organizeKirtan === "No" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      {errors.organizeKirtan && (
                        <p className="text-red text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.organizeKirtan}
                        </p>
                      )}
                    </div>

                    {/* Question 7: Would you like weekly or monthly Srimad Bhagavad Gita sessions in your locality? */}
                    <div className="border-b border-gray-100 pb-3">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 lg:gap-3">
                        <label className="text-lg md:text-xl font-black text-gray-800 md:max-w-xl">
                          Would you like weekly or monthly Srimad Bhagavad Gita sessions in your locality?
                        </label>
                        <div className="flex bg-[#FAF9F6] border border-gray-200 rounded-xl p-1 w-full md:w-[200px] h-[44px] items-center relative flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("weeklyGitaSessions", "Yes")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.weeklyGitaSessions === "Yes" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("weeklyGitaSessions", "No")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.weeklyGitaSessions === "No" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      {errors.weeklyGitaSessions && (
                        <p className="text-red text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.weeklyGitaSessions}
                        </p>
                      )}
                    </div>

                    {/* Question 8: Would you like to celebrate Krishna Janmashtami in your locality with Kirtan and Prasadam? */}
                    <div>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 lg:gap-3">
                        <label className="text-lg md:text-xl font-black text-gray-800 md:max-w-xl">
                          Would you like to celebrate Krishna Janmashtami in your locality with Kirtan and Prasadam?
                        </label>
                        <div className="flex bg-[#FAF9F6] border border-gray-200 rounded-xl p-1 w-full md:w-[200px] h-[44px] items-center relative flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("celebrateJanmashtami", "Yes")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.celebrateJanmashtami === "Yes" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSpiritualQuestionSelect("celebrateJanmashtami", "No")}
                            className={`flex-1 text-center py-1.5 text-base md:text-lg font-black rounded-lg transition-all ${
                              form.celebrateJanmashtami === "No" ? "bg-darkred text-white shadow" : "text-gray-500"
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      {errors.celebrateJanmashtami && (
                        <p className="text-red text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.celebrateJanmashtami}
                        </p>
                      )}
                    </div>

                  </div>
                </div>

                {/* SECTION 4: Communication Preference (Tighter gap) */}
                <div 
                  id="communication-preferences"
                  ref={commRef}
                  className="bg-white rounded-3xl shadow-xl shadow-amber-900/5 border border-amber-500/10 p-3 sm:p-2 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red via-orange to-yellow-500" />
                  
                  <div className="flex items-center gap-3.5 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-orange/10 text-orange flex items-center justify-center font-semibold text-lg flex-shrink-0">
                      4
                    </div>
                    <div>
                      <h2 className="text-lg md:text-3xl font-black text-darkred">Communication Preference</h2>
                      <p className="text-sm text-gray-500">Preferred Contact Method:</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5 sm:gap-2 lg:gap-4">
                    
                    {/* Phone Card */}
                    <div
                      onClick={() => handleCommPreferenceSelect("Phone")}
                      className={`border rounded-2xl p-2 sm:p-3 cursor-pointer transition-all duration-300 text-center flex flex-col items-center gap-1 shadow-sm ${
                        form.communicationPreference === "Phone" 
                          ? "border-orange bg-orange/5 ring-1 ring-orange" 
                          : "border-gray-200 hover:border-orange/50 hover:bg-[#FAF9F6]"
                      }`}
                    >
                      <div className={`p-2 sm:p-3.5 rounded-full ${form.communicationPreference === "Phone" ? "bg-orange text-white" : "bg-orange/10 text-orange"}`}>
                        <Phone className="w-5 h-5 sm:w-7 h-7" />
                      </div>
                      <h4 className="font-semibold text-sm sm:text-lg md:text-xl text-gray-800">Phone</h4>
                      <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">Regular cellular phone calls</p>
                    </div>

                    {/* WhatsApp Card */}
                    <div
                      onClick={() => handleCommPreferenceSelect("WhatsApp")}
                      className={`border rounded-2xl p-2 sm:p-3 cursor-pointer transition-all duration-300 text-center flex flex-col items-center gap-1 shadow-sm ${
                        form.communicationPreference === "WhatsApp" 
                          ? "border-orange bg-orange/5 ring-1 ring-orange" 
                          : "border-gray-200 hover:border-orange/50 hover:bg-[#FAF9F6]"
                      }`}
                    >
                      <div className={`p-2 sm:p-3.5 rounded-full ${form.communicationPreference === "WhatsApp" ? "bg-orange text-white" : "bg-orange/10 text-orange"}`}>
                        <MessageSquare className="w-5 h-5 sm:w-7 h-7" />
                      </div>
                      <h4 className="font-semibold text-sm sm:text-lg md:text-xl text-gray-800">WhatsApp</h4>
                      <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">Direct WhatsApp chats & alerts</p>
                    </div>

                    {/* Email Card */}
                    <div
                      onClick={() => handleCommPreferenceSelect("Email")}
                      className={`border rounded-2xl p-2 sm:p-3 cursor-pointer transition-all duration-300 text-center flex flex-col items-center gap-1 shadow-sm ${
                        form.communicationPreference === "Email" 
                          ? "border-orange bg-orange/5 ring-1 ring-orange" 
                          : "border-gray-200 hover:border-orange/50 hover:bg-[#FAF9F6]"
                      }`}
                    >
                      <div className={`p-2 sm:p-3.5 rounded-full ${form.communicationPreference === "Email" ? "bg-orange text-white" : "bg-orange/10 text-orange"}`}>
                        <Mail className="w-5 h-5 sm:w-7 h-7" />
                      </div>
                      <h4 className="font-semibold text-sm sm:text-lg md:text-xl text-gray-800">Email</h4>
                      <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">Updates to your email address</p>
                    </div>

                  </div>
                  {errors.communicationPreference && (
                    <p className="text-red text-sm mt-2 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.communicationPreference}
                    </p>
                  )}
                </div>

                {/* SECTION 5: Remarks */}
                <div 
                  id="remarks"
                  ref={remarksRef}
                  className="bg-white rounded-3xl shadow-xl shadow-amber-900/5 border border-amber-500/10 p-3 sm:p-2 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red via-orange to-yellow-500" />
                  
                  <div className="flex items-center gap-3.5 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-orange/10 text-orange flex items-center justify-center font-semibold text-lg flex-shrink-0">
                      5
                    </div>
                    <div>
                      <h2 className="text-lg md:text-3xl font-black text-darkred">Remarks</h2>
                      <p className="text-sm text-gray-500">Provide any additional comments or notes below.</p>
                    </div>
                  </div>

                  <div className="relative">
                    <textarea
                      id="remarks"
                      name="remarks"
                      value={form.remarks}
                      onChange={handleInputChange}
                      placeholder="Additional Comments / Notes"
                      rows={4}
                      className="peer w-full bg-[#FAF9F6] text-lg md:text-xl font-normal text-gray-800 border border-gray-200 rounded-xl px-4 py-3 outline-none transition-all duration-300 focus:border-orange focus:ring-2 focus:ring-orange/20 resize-none"
                    />
                  </div>
                </div>

                {/* Form Submission Call to Action (Tighter margins) */}
                <div className="bg-gradient-to-r from-red/5 to-orange/5 border border-orange/20 rounded-3xl p-3 lg:p-6 flex flex-col md:flex-row items-center justify-between gap-4 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-orange/10 to-transparent rounded-bl-full pointer-events-none" />
                  
                  <div className="space-y-1 text-center md:text-left relative z-10">
                    <h3 className="font-black text-xl lg:text-3xl text-darkred">Chant Hare Krishna and Be Happy!</h3>
                    <p className="text-lg text-gray-500">Begin your spiritual journey with ISKCON Vijayawada.</p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full md:w-auto bg-gradient-to-r from-red to-orange hover:from-darkred hover:to-orange text-white font-semibold px-6 lg:px-10 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-xl sm:text-2xl min-w-[240px]"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span>Registering...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-6 h-6" />
                        <span>Submit Registration</span>
                      </>
                    )}
                  </button>
                </div>

              </form>
            </div>

          </div>
        </div>

        {/* Closing Spiritual Section / Footer Details */}
        <div className="bg-white border-t border-gray-100 py-8 text-center space-y-4 relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-transparent via-orange/30 to-transparent" />
          
          <div className="max-w-3xl mx-auto space-y-3 px-4">
            <h3 className="font-dm-serif text-2xl md:text-4xl text-darkred font-bold">Thank you for joining us!</h3>
            
            <p className="text-xl md:text-3xl font-semibold text-orange font-josefin tracking-wide animate-pulse uppercase">
              Chant Hare Krishna and Be Happy!
            </p>
            
            <p className="text-base sm:text-lg font-semibold text-gray-600 tracking-wider">
              ISKCON – International Society for Krishna Consciousness, Vijayawada
            </p>
          </div>
        </div>

        <Footer />

        {/* Successful Registration Modal Overlay */}
        <AnimatePresence>
          {showSuccessModal && (
            <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 350 }}
                className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative shadow-2xl border border-amber-500/20"
              >
                {/* Accent Header Line */}
                <div className="h-2 bg-gradient-to-r from-red via-orange to-yellow-500 w-full" />
                
                <div className="p-6 sm:p-8 space-y-6">
                  
                  {/* Icon and Title */}
                  <div className="text-center space-y-3">
                    <div className="w-12 h-12 bg-orange/10 text-orange rounded-full flex items-center justify-center mx-auto shadow-inner relative">
                      <Sparkles className="w-6 h-6 text-orange" />
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 10, ease: "linear" }}
                        className="absolute inset-0 border border-dashed border-orange rounded-full"
                      />
                    </div>

                    <h2 className="font-dm-serif text-3xl sm:text-4xl font-black text-darkred">Registration Successful!</h2>
                    <p className="text-sm sm:text-base text-gray-500 leading-normal max-w-md mx-auto">
                      Thank you for registering with ISKCON Vijayawada. Your congregation details have been recorded successfully.
                    </p>
                  </div>

                  {/* Summary Details Container */}
                  <div className="bg-[#FAF9F6] border border-orange/10 rounded-2xl p-4 sm:p-5 space-y-4">
                    <div className="border-b border-orange/10 pb-3 flex justify-between items-center text-base font-bold text-darkred">
                      <span>Devotee Details</span>
                      <span className="text-xs text-orange bg-orange/10 px-2.5 py-0.5 rounded-full font-semibold">
                        {form.gender} ({form.age} yrs)
                      </span>
                    </div>

                    {/* Grid Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
                      <p><strong className="text-gray-900">Full Name:</strong> {form.fullName}</p>
                      <p><strong className="text-gray-900">Mobile:</strong> +91 {form.mobileNumber}</p>
                      <p><strong className="text-gray-900">WhatsApp:</strong> +91 {form.whatsAppNumber}</p>
                      <p><strong className="text-gray-900">Email ID:</strong> {form.emailId}</p>
                      <p><strong className="text-gray-900">Occupation:</strong> {form.occupation}</p>
                      <p><strong className="text-gray-900">Language:</strong> {form.preferredLanguage === "Other" ? form.preferredLanguageOther : form.preferredLanguage}</p>
                      <p className="sm:col-span-2"><strong className="text-gray-900">Address:</strong> {form.address}</p>
                    </div>

                    {/* Connecting Selection */}
                    {form.connectPreferences.length > 0 && (
                      <div className="border-t border-orange/10 pt-3 space-y-1.5">
                        <p className="text-sm font-bold text-darkred">Connect Preferences Selected:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {form.connectPreferences.map(id => {
                            const label = CONGREGATION_CONNECT_OPTIONS.find(opt => opt.id === id)?.label || id;
                            return (
                              <span key={id} className="text-xs bg-orange/10 text-orange font-bold px-2.5 py-0.5 rounded-full border border-orange/20">
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Spiritual Background Selection */}
                    <div className="border-t border-orange/10 pt-3 space-y-2 text-sm text-gray-700">
                      <p className="font-bold text-darkred">Spiritual Background Summary:</p>
                      <ul className="space-y-1 pl-0 m-0 text-xs md:text-sm list-none">
                        <li className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                          <span>Visited ISKCON before: <strong className="text-gray-900">{form.visitedIskcon}</strong></span>
                        </li>
                        <li className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                          <span>How did you know about Ratha Yatra: <strong className="text-gray-900">{form.knowAboutRathaYatra}</strong></span>
                        </li>
                        <li className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                          <span>Interested in Bhagavad Gita learning: <strong className="text-gray-900">{form.interestedInGita}</strong></span>
                        </li>
                        <li className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                          <span>Would like Home Visit: <strong className="text-gray-900">{form.homeVisitInvitation}</strong></span>
                        </li>
                      </ul>
                    </div>

                    {/* Preferred Contact Mode */}
                    <div className="border-t border-orange/10 pt-3 flex justify-between items-center text-sm text-gray-700">
                      <span><strong className="text-gray-900">Communication Preference:</strong></span>
                      <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-0.5 rounded-full uppercase">
                        {form.communicationPreference}
                      </span>
                    </div>

                  </div>

                  <p className="text-xs md:text-sm text-gray-400 text-center leading-relaxed">
                    We will send regular devotional updates and event notifications to your chosen contact method. Hare Kṛṣṇa!
                  </p>

                  <button
                    type="button"
                    onClick={handleModalClose}
                    className="w-full bg-gradient-to-r from-red to-orange hover:from-darkred hover:to-orange text-white font-semibold py-3.5 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 text-base uppercase tracking-wider"
                  >
                    Chant Hare Krishna and Be Happy
                  </button>

                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

      </div>
    </>
  );
};

export default CongregationPreaching;
